import {
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type CSSProperties,
} from "react";
import { preload } from "react-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectAdminBackgroundUrl,
  selectAdminIsImageLoaded,
  setAdminImageLoaded,
} from "@/store/slices/adminBackgroundSlice/adminBackgroundSlice";

import {
  ADMIN_DEFAULT_BACKGROUND_URL,
  ADMIN_OVERLAY_URL,
  ADMIN_FALLBACK_COLOR,
  ADMIN_BG_OPACITY,
  ADMIN_OVERLAY_OPACITY,
  ADMIN_TRANSITION_DURATION,
} from "@/layouts/AdminBackgroundLayout/AdminBackgroundLayout.types";
import { logger } from "../utils";

interface UseAdminBackroundReturn {
  /* Css variables to apply to layout container. Apply via: style={backgroundStyles}  */
  backgroundStyles: CSSProperties;

  backgroundImageUrl: string | null;
  effectiveUrl: string;
  fallbackColor: string;
  isImageLoaded: boolean;
  isFetching: boolean;
  isUploading: boolean;
  isRemoving: boolean;
  isUsingCustomBackground: boolean;
  upload: (file: File) => Promise<void>;
  remove: () => Promise<void>;
}

export const useAdminBackground = (): UseAdminBackroundReturn => {
  const dispatch = useAppDispatch();
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Redux
  const customUrl = useAppSelector(selectAdminBackgroundUrl);
  const isImageLoaded = useAppSelector(selectAdminIsImageLoaded);

  // Effective Url ( custom or default)
  const effectiveUrl = customUrl ?? ADMIN_DEFAULT_BACKGROUND_URL;

  // React Query
  const isFetching = false; // here we call the useAdminBackgroundSettings();
  const uploadMutation = null; //useUploadAdminBackground();
  const removeMutation = null; //useRemoveAdminBackground();

  // LazyLoad Image
  useEffect(() => {
    if (!effectiveUrl) return;
    // Cleanup previous image
    if (imageRef.current) {
      imageRef.current.onload = null;
      imageRef.current.onerror = null;
      imageRef.current.src = "";
      imageRef.current = null;
    }
    // Reset loaded state when url changes
    dispatch(setAdminImageLoaded(false));

    // Preload Reacr 19 preload for resource hints
    preload(effectiveUrl, {
      as: "image",
      fetchPriority: "high",
      crossOrigin: "anonymous",
    });
    // Create image element to track loading
    const img = new Image();
    imageRef.current = img;
    img.crossOrigin = "anonymous";

    img.onload = () => dispatch(setAdminImageLoaded(true));
    img.onerror = () => dispatch(setAdminImageLoaded(false));
    // start loading
    img.src = effectiveUrl;
    // Cleanup unmount or URL change
    return () => {
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
        imageRef.current.src = "";
        imageRef.current = null;
      }
    };
  }, [effectiveUrl, dispatch]);

  // ================================================================
  // Css variables for layout
  // ================================================================
  const backgroundStyles = useMemo(
    (): CSSProperties =>
      ({
        // Fallback color (always visible as base layer)
        "--admin-fallback-color": ADMIN_FALLBACK_COLOR,
        //Primary background image (::before)
        "--admin-bg-image": isImageLoaded ? `url(${effectiveUrl})` : "none",
        "--admin-bg-opacity": isImageLoaded ? ADMIN_BG_OPACITY : 0,
        // Secondary overlay image (::after) - always static
        "--admin-overlay-image": `url(${ADMIN_OVERLAY_URL})`,
        "--admin-overlay-opacity": ADMIN_OVERLAY_OPACITY,
        "--admin-transition-duration": `${ADMIN_TRANSITION_DURATION}`,
      }) as CSSProperties,
    [effectiveUrl, isImageLoaded],
  );
  const upload = useCallback(
    async (file: File) => {
      return Promise.reject();
      logger.info("file", file);
      // await uploadMutation!.mutateAsync(file);
    },
    [uploadMutation],
  );

  const remove = useCallback(async () => {
    return Promise.reject();
    //await removeMutation.mutateAsync();
  }, [removeMutation]);

  return {
    backgroundStyles,
    backgroundImageUrl: isImageLoaded ? effectiveUrl : null,
    effectiveUrl,
    fallbackColor: ADMIN_FALLBACK_COLOR,
    isImageLoaded,
    isFetching,
    isUploading: false,
    isRemoving: false,
    isUsingCustomBackground: customUrl !== null,
    upload,
    remove,
  };
};
